import VolumeDown from '@mui/icons-material/VolumeDown';
import VolumeUp from '@mui/icons-material/VolumeUp';
import { Theme } from '@mui/material';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import * as React from 'react';
import { useAppSelector } from '../../app/hooks';
import { selectIsPlaying } from '../../selectors/message';
import { audioPlayer } from '../../utils/asrcAudio';

// HITL 3.0 volume control hosted inside the header Player's hover popover.
// Uncontrolled (the thumb tracks the cursor natively). The level itself lives on
// the `audioPlayer` singleton (HITL-1472), so it survives both the popover
// closing/reopening and the AudioContext being torn down and rebuilt. The OLD
// inline transcript volume control is the separate `Audio/VolumeSlider`, which
// now reads and writes the same player state.
const useStyles = makeStyles<Theme>(({}) => ({
  boxClass: {
    width: '100%',
    height: '40px',
  },
}));

const MIN_DB = -30;
const MAX_DB = 30;

interface HeaderVolumeSliderProps {
  // Mirror of the player's mute state, so the header icon re-renders when it
  // changes. The player stays the source of truth for the actual gain.
  isMuted: boolean;
  onMutedChange: (muted: boolean) => void;
}

export const HeaderVolumeSlider = ({
  isMuted,
  onMutedChange,
}: HeaderVolumeSliderProps) => {
  const { boxClass } = useStyles();
  const isPlaying = useAppSelector(selectIsPlaying);

  // Remount the uncontrolled slider when the icons jump it to min/max.
  const [sliderKey, setSliderKey] = React.useState(0);

  // Uncontrolled: push the level straight to the player on drag WITHOUT
  // re-rendering, so the thumb tracks the cursor natively (smooth/immediate).
  const handleChange = (_event: Event, newValue: number | Array<number>) => {
    audioPlayer.setVolumeDb(newValue as number);
    // Only fire on the mute -> unmute transition (not every drag tick).
    if (isMuted) onMutedChange(false);
  };

  const handleMute = () => {
    // Drop the level to min as well as muting, so the remounted thumb shows it.
    audioPlayer.setVolumeDb(MIN_DB);
    audioPlayer.setMuted(true);
    onMutedChange(true);
    setSliderKey((k) => k + 1);
  };
  const handleFull = () => {
    audioPlayer.setVolumeDb(MAX_DB);
    onMutedChange(false);
    setSliderKey((k) => k + 1);
  };

  if (!isPlaying) return null;

  return (
    <Box className={boxClass}>
      <Stack spacing={2} direction="row" sx={{ mb: 1 }} alignItems="center">
        <VolumeDown
          sx={{ cursor: 'pointer' }}
          onClick={handleMute}
          aria-label="Mute"
        />
        <Slider
          key={sliderKey}
          aria-label="Volume"
          min={MIN_DB} // decibel
          max={MAX_DB} // decibel
          defaultValue={audioPlayer.volumeLevelDb}
          onChange={handleChange}
        />
        <VolumeUp
          sx={{ cursor: 'pointer' }}
          onClick={handleFull}
          aria-label="Full volume"
        />
      </Stack>
    </Box>
  );
};
